/**
 * Saved Searches — client-side search snapshots.
 *
 * Persisted to localStorage. Reactive across components in the same tab via a
 * custom window event (same pattern as featureFlags). Zero Supabase dependency —
 * this is personal, per-browser storage that doesn't need to be shared.
 *
 * Shape mirrors the AdvancedSearch page state so it can be round-tripped
 * perfectly: search query, facet selections, date ranges, sort.
 */

import { useState, useEffect } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface SavedSearch {
  id: string;
  name: string;
  description?: string;
  /** Free-text search query */
  searchQuery: string;
  /** Facet selections: field → array of selected values (serializable Set) */
  facetValues: Record<string, string[]>;
  /** Date range filters: field → [isoStart, isoEnd] */
  dateRanges: Record<string, [string, string]>;
  /** Sort field key */
  sortField: string;
  /** Sort direction */
  sortDir: 'asc' | 'desc';
  /** Result count at the time the search was saved — display hint only */
  resultCount?: number;
  createdAt: string;
  updatedAt: string;
}

export type SavedSearchInput = Omit<SavedSearch, 'id' | 'createdAt' | 'updatedAt'>;

// ============================================================================
// Storage
// ============================================================================

const STORAGE_KEY = 'pp_saved_searches';
const CHANGE_EVENT = 'pp_saved_searches_changed';

function readFromStorage(): SavedSearch[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as SavedSearch[];
  } catch {
    return [];
  }
}

function writeToStorage(searches: SavedSearch[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(searches));
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  } catch {
    /* quota exceeded or SSR — silently ignore */
  }
}

// ============================================================================
// Public API (imperative, for non-hook contexts)
// ============================================================================

export function getSavedSearches(): SavedSearch[] {
  return readFromStorage();
}

export function createSavedSearch(input: SavedSearchInput): SavedSearch {
  const now = new Date().toISOString();
  const search: SavedSearch = { ...input, id: crypto.randomUUID(), createdAt: now, updatedAt: now };
  const existing = readFromStorage();
  writeToStorage([search, ...existing]);
  return search;
}

export function updateSavedSearch(id: string, changes: Partial<Pick<SavedSearch, 'name' | 'description'>>): void {
  const existing = readFromStorage();
  writeToStorage(
    existing.map((s) =>
      s.id === id ? { ...s, ...changes, updatedAt: new Date().toISOString() } : s,
    ),
  );
}

export function deleteSavedSearch(id: string): void {
  writeToStorage(readFromStorage().filter((s) => s.id !== id));
}

// ============================================================================
// React hook
// ============================================================================

export function useSavedSearches() {
  const [searches, setSearches] = useState<SavedSearch[]>(() => readFromStorage());

  useEffect(() => {
    const refresh = () => setSearches(readFromStorage());
    window.addEventListener(CHANGE_EVENT, refresh);
    // Also sync across tabs via the native storage event
    window.addEventListener('storage', (e) => {
      if (e.key === STORAGE_KEY) refresh();
    });
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh);
    };
  }, []);

  const save = (input: SavedSearchInput): SavedSearch => createSavedSearch(input);
  const remove = (id: string): void => deleteSavedSearch(id);
  const rename = (id: string, name: string, description?: string): void =>
    updateSavedSearch(id, { name, description });

  return { searches, save, remove, rename };
}

// ============================================================================
// URL helpers — encode a saved search id into the search page URL
// ============================================================================

export function savedSearchHref(id: string): string {
  return `/prioritization/search?saved=${encodeURIComponent(id)}`;
}
